import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';
import { CheckCircle2, Shield, Cloud, Users, Zap } from 'lucide-react';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  cta: string;
  ctaLink: string;
  disabled?: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Free',
    price: 'Beta Access',
    period: '',
    description: 'Perfect for individuals getting started with personal health management.',
    features: [
      'Store unlimited health records',
      'Personal cloud storage',
      'End-to-end encryption',
      'Mobile & web access',
      'Basic health timeline',
      'Export records as PDF',
    ],
    badge: 'Beta',
    cta: 'Join Beta',
    ctaLink: '/register',
    disabled: false,
  },
  {
    name: 'Premium',
    price: 'Coming Soon',
    period: '',
    description: 'Advanced features for health-conscious individuals and families.',
    features: [
      'Everything in Free',
      'Extra cloud storage',
      'Secured AI health tools',
      'Family member profiles',
      'Provider sharing controls',
      'Priority support',
      'Advanced health insights',
      'Medication reminders',
      'Wellness hub access',
    ],
    highlighted: true,
    badge: 'Coming Soon',
    cta: 'Coming Soon',
    ctaLink: '#',
    disabled: true,
  },
  {
    name: 'Enterprise',
    price: 'Coming Soon',
    period: '',
    description: 'For healthcare providers and organizations managing patient records.',
    features: [
      'Everything in Premium',
      'Secured AI health tools',
      'Unlimited staff accounts',
      'Patient management system',
      'Compliance tools',
      'Custom integrations',
      'Dedicated support',
      'Analytics dashboard',
      'White-label options',
    ],
    badge: 'Coming Soon',
    cta: 'Coming Soon',
    ctaLink: '#',
    disabled: true,
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-[#D64463] selection:text-white overflow-x-hidden">
      <UniversalHeader className="bg-[#0F172A] border-transparent text-white" />

      {/* Hero Section - Clean & Premium */}
      <section className="relative pt-32 pb-24 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-50">

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(#94a3b8 1px, transparent 1px), linear-gradient(90deg, #94a3b8 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        </div>

        {/* Floating gradient accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-blue-50/80 to-transparent rounded-[100%] blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm animate-fade-in-up mb-8">
            <span className="text-sm font-bold text-[#D64463] tracking-wide uppercase">Early Access</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-6 tracking-tight animate-fade-in-up animation-delay-100">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D64463] to-[#9C27B0]">
              Join our beta
            </span>
            <br />
            and shape the future
          </h1>

          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
            We're currently in beta. All features are free for early adopters. 
            Help us build Africa's most trusted health record platform.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-24 relative bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto -mt-32">
            {pricingTiers.map((tier) => (
              <Card
                key={tier.name}
                className={`relative border-0 shadow-2xl flex flex-col transition-all duration-300 ${tier.highlighted
                    ? 'scale-105 z-10 bg-white ring-1 ring-slate-200'
                    : 'bg-white/80 backdrop-blur-sm mt-4 lg:mt-8 hover:-translate-y-2 text-slate-600'
                  }`}
              >
                {tier.highlighted && tier.badge && (
                  <div className="absolute -top-5 left-0 right-0 flex justify-center">
                    <div className="bg-[#D64463] text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg shadow-rose-200 uppercase tracking-wide">
                      {tier.badge}
                    </div>
                  </div>
                )}

                <CardHeader className="text-center pt-10 pb-2">
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    {tier.name}
                  </CardTitle>
                  <div className="mt-6 mb-2">
                    <span className="text-4xl font-extrabold text-slate-900 tracking-tight">{tier.price}</span>
                    {tier.period && <span className="text-slate-500 ml-1 font-medium">{tier.period}</span>}
                  </div>
                  <CardDescription className="text-slate-500 h-10">
                    {tier.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-8 flex-1 flex flex-col">
                  <div className="space-y-4 mb-8 flex-1">
                    {tier.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className={`h-5 w-5 flex-shrink-0 mt-0.5 ${tier.highlighted ? 'text-[#D64463]' : 'text-slate-400'
                          }`} />
                        <span className="text-slate-700 text-sm font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Buttons removed as requested */}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust/App Features Grid */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why choose CareVault
            </h2>
            <p className="text-slate-500">More than just storage. A complete health operating system.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Bank-Grade Security</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Your data is encrypted with AES-256 protocols, ensuring only you hold the keys.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-purple-50 flex items-center justify-center mb-6">
                <Cloud className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Cloud Synced</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Access your records from any device, anywhere in the world, instantly.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-rose-50 flex items-center justify-center mb-6">
                <Zap className="h-6 w-6 text-rose-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Instant Sharing</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Generate temporary, secure links for doctors during emergencies or checkups.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-6">
                <Users className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">Family First</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Manage your children's and elderly parents' records from a single account.
              </p>
            </div>
          </div>
        </div>
      </section>

      <UniversalFooter />
    </div>
  );
}
